import { createContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Internal Linking Manager (client side).
 * Editable text may contain inline links written as [anchor text](/approved-page).
 * The server is the authority: it validates on save and strips links to
 * unpublished pages from public responses. These helpers give editors instant
 * feedback and render links as crawlable <a href> anchors.
 */

export const LINK_RE = /\[([^[\]\n]{1,150})\]\(([^()\s]{0,300})\)/g;

const PROHIBITED_PATHS = {
  "/commercial-cleaning-sacramento": "Duplicate of Commercial Cleaning; use /commercial-cleaning",
  "/property-management-cleaning-sacramento": "Prohibited page",
  "/junk-removal": "Duplicate of Junk Removal; use /junk-removal-sacramento",
};
const PROHIBITED_SUBSTRINGS = [
  ["emergentagent.com", "Preview URL"],
  ["property-management", "Prohibited page"],
  ["preview=", "Preview URL"],
];
const VAGUE = new Set(["click here", "here", "read more", "learn more", "more", "this page", "this", "link", "click"]);

// Extra phrases an editor might type for each service page (for link suggestions).
const SYNONYMS = {
  "/junk-removal-sacramento": ["junk removal", "junk hauling"],
  "/monthly-residential-cleaning-sacramento": ["monthly residential cleaning", "monthly cleaning", "monthly house cleaning"],
  "/office-cleaning-sacramento": ["office cleaning"],
  "/move-out-cleaning-sacramento": ["move-out cleaning", "move out cleaning"],
  "/pre-listing-cleaning-sacramento": ["pre-listing cleaning", "pre listing cleaning"],
  "/commercial-cleaning": ["commercial cleaning"],
};

/** The page being edited (e.g. "/office-cleaning-sacramento"); excluded from link pickers/suggestions. */
export const LinkSelfContext = createContext("");

let cache = null;
let inflight = null;

export function fetchInternalLinks(force = false) {
  if (force) cache = null;
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = api
      .get("/public/internal-links")
      .then((r) => {
        cache = Array.isArray(r.data) ? r.data : [];
        return cache;
      })
      .catch(() => cache || [])
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Approved link targets: [{label, url, kind: "service"|"page", live}] */
export function useInternalLinks() {
  const [targets, setTargets] = useState(cache || []);
  useEffect(() => {
    let off = false;
    fetchInternalLinks().then((t) => !off && setTargets(t));
    return () => {
      off = true;
    };
  }, []);
  return targets;
}

export function targetFor(url, targets) {
  const path = (url || "").split("#")[0];
  return (targets || []).find((t) => t.url === path) || null;
}

/** Returns null or {level: "error"|"warning", message}. mode: "internal" | "cta" */
export function checkUrl(url, targets, mode = "internal") {
  const raw = url || "";
  const u = raw.trim();
  if (!u) return null;
  const low = u.toLowerCase();
  for (const [sub, why] of PROHIBITED_SUBSTRINGS) {
    if (low.includes(sub)) return { level: "error", message: `Prohibited link (${why}).` };
  }
  if (mode === "cta" && (low.startsWith("tel:") || low.startsWith("mailto:"))) return null;
  if (/^https?:\/\/(www\.)?pacleans\.com(\/|$)/i.test(u)) {
    const rel = u.replace(/^https?:\/\/(www\.)?pacleans\.com/i, "") || "/";
    return { level: "error", message: `Use the relative link ${rel} instead of the full web address.` };
  }
  if (low.startsWith("http://") || low.startsWith("https://")) {
    return mode === "cta" ? null : { level: "error", message: "Only approved internal pages can be linked here." };
  }
  if (u !== raw || u.includes(" ")) return { level: "error", message: "Link has extra spaces." };
  if (u.includes("//")) return { level: "error", message: "Malformed link (double slash)." };
  if (u.includes("?")) return { level: "error", message: "Links with ? query strings are not allowed." };
  const path = u.split("#")[0];
  if (!path.startsWith("/")) return { level: "error", message: "Internal links must start with / (e.g. /request-estimate)." };
  if (path !== path.toLowerCase()) return { level: "error", message: "Use lowercase links." };
  if (path.startsWith("/admin") || path.startsWith("/api")) {
    return { level: "error", message: "Admin or system links cannot be used on the website." };
  }
  if (PROHIBITED_PATHS[path]) return { level: "error", message: `Prohibited link: ${PROHIBITED_PATHS[path]}.` };
  if (path !== "/" && path.endsWith("/")) return { level: "error", message: "Remove the trailing slash." };
  const t = targetFor(path, targets);
  if (!t) return { level: "error", message: "Not on the approved internal page list." };
  if (!t.live) {
    return { level: "warning", message: `${t.label} is not published yet; this link stays hidden on the live site until it is.` };
  }
  return null;
}

/** Validate inline links inside a text field. */
export function checkText(text, targets) {
  const issues = [];
  const counts = {};
  const src = text || "";
  for (const m of src.matchAll(LINK_RE)) {
    const anchor = m[1].trim();
    const url = m[2];
    const iss = checkUrl(url, targets, "internal");
    if (iss) issues.push({ ...iss, url, anchor });
    if (VAGUE.has(anchor.toLowerCase().replace(/[ .!]+$/, ""))) {
      issues.push({ level: "warning", url, anchor, message: `Use descriptive link text instead of "${anchor}".` });
    }
    counts[url] = (counts[url] || 0) + 1;
  }
  Object.entries(counts).forEach(([url, n]) => {
    if (n > 1) issues.push({ level: "warning", url, message: `${url} is linked ${n} times here; link it once.` });
  });
  if (/emergentagent\.com|[?&]preview=/i.test(src.replace(LINK_RE, ""))) {
    issues.push({ level: "error", url: "", message: "Remove the preview web address from this text." });
  }
  if (/\[[^\]\n]*\]\s*\(/.test(src.replace(LINK_RE, ""))) {
    issues.push({ level: "error", url: "", message: "A link is not written correctly. Use [link text](/page)." });
  }
  return issues;
}

/** Suggest links for service names typed as plain text (first unlinked mention only). */
export function suggestLinks(text, targets) {
  const src = text || "";
  const linkedUrls = new Set([...src.matchAll(LINK_RE)].map((m) => m[2].split("#")[0]));
  // Blank out existing links so we never suggest inside them.
  const plain = src.replace(LINK_RE, (m) => " ".repeat(m.length));
  const out = [];
  (targets || [])
    .filter((t) => t.kind === "service" && t.live && !linkedUrls.has(t.url))
    .forEach((t) => {
      const phrases = SYNONYMS[t.url] || [t.label.toLowerCase()];
      let best = null;
      phrases.forEach((p) => {
        const re = new RegExp(`(^|[^a-z0-9-])(${p.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")})(?![a-z0-9-])`, "i");
        const m = re.exec(plain);
        if (m) {
          const start = m.index + m[1].length;
          if (!best || start < best.start) best = { start, end: start + m[2].length, phrase: src.slice(start, start + m[2].length) };
        }
      });
      if (best) out.push({ ...best, url: t.url, label: t.label });
    });
  return out.sort((a, b) => a.start - b.start);
}

/** Wrap text[start:end] as a link. */
export function wrapLink(text, start, end, url) {
  const src = text || "";
  const anchor = src.slice(start, end).trim();
  if (!anchor) return src;
  return `${src.slice(0, start)}[${anchor}](${url})${src.slice(end)}`;
}

/** Split text into plain-text and link tokens. */
export function parseLinkedText(text) {
  const src = text || "";
  const tokens = [];
  let last = 0;
  for (const m of src.matchAll(LINK_RE)) {
    if (m.index > last) tokens.push({ type: "text", value: src.slice(last, m.index) });
    tokens.push({ type: "link", anchor: m[1], url: m[2] });
    last = m.index + m[0].length;
  }
  if (last < src.length) tokens.push({ type: "text", value: src.slice(last) });
  return tokens;
}

/** Remove link syntax, keeping the anchor text. */
export function stripLinks(text) {
  return (text || "").replace(LINK_RE, (_m, a) => a);
}
